// src/components/templates/diary/DiaryLayout.jsx
import React, { useState } from 'react';
import { 
  LayoutDashboard, Calendar, Settings, Menu, StickyNote, FolderOpen, Wrench,
  ChevronLeft, ChevronRight, Book, Users, Briefcase, X, FileCheck,
  Home, Search, Bell, HelpCircle, ClipboardList, Share2, Target, GraduationCap,
  ToolCase
} from 'lucide-react';
import { ViewState } from './UIComponents';
import HeaderLogoTitle from '../shared/HeaderLogoTitle';
import UserProfile from '../shared/SharedUserProfile';

// --- SIDEBAR NAV ITEM ---
const NavItem = ({ icon: Icon, label, isActive, onClick, badge, customStyling }) => (
  <button
    onClick={onClick}
    className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 cursor-pointer w-full text-left font-medium whitespace-nowrap overflow-hidden ${
      isActive
        ? 'bg-gradient-to-r from-gray-100/70 to-gray-300/80 text-gray-700 shadow-md translate-x-1'
        : 'text-gray-400 hover:border-gray-300 hover:text-white hover:translate-x-1'
    }`}
    style={
      isActive
        ? {
            boxShadow: `
              inset 0 1px 3px rgba(0, 0, 0, 0.3),
              0 0 8px rgba(255, 255, 255, 0.3)
            `,
          }
        : {}
    }
  >
    <Icon className="w-5 h-5 shrink-0" />
    <span className="flex-1">{label}</span>

    {badge !== undefined && badge > 0 && (
      <span
        className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${
          isActive ? 'bg-red-600 text-white' : 'text-red-500 bg-red-100'
        }`}

        style={{ backgroundColor: isActive ? customStyling.primaryColor : undefined }}
      >
        {badge}
      </span>
    )}
  </button>

);

// --- SPIRAL BINDING ---
const SpiralBinding = ({ ringCount = 35 }) => {
  const rings = Array.from({ length: ringCount });

  return (
    <div className="w-8 bg-[#dd5b0b] flex flex-col items-center py-6 gap-6 border-r border-gray-950 relative shadow-2xl z-20 overflow-visible rounded-l-md shrink-0">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-20 bg-[radial-gradient(#ffffff33_1px,transparent_1px)] [background-size:8px_8px] pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-px bg-white/5 pointer-events-none" />
      
      {rings.map((_, i) => (
        <div key={i} className="relative w-full h-6 flex items-center justify-end shrink-0">
          {/* The Hole */}
          <div className="absolute w-4 h-4 rounded-full bg-black shadow-[inset_0_1px_3px_rgba(255,255,255,0.15)] border border-white/5 z-0" />
          
          {/* The Spiral Wire */}
          <div 
            className="absolute w-8 h-1.5 rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.5)] transform -rotate-12 translate-x-[-2px] z-10"
            style={{
              background: 'linear-gradient(to bottom, #78350f 0%, #fde047 40%, #fde047 60%, #78350f 100%)'
            }}
          >
            {/* Reflection highlight */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-white/40 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
};

// --- MOBILE BOTTOM NAV ---
const MobileBottomNav = ({ currentView, onNavigate }) => {
  const navItems = [
    { view: ViewState.DASHBOARD, icon: Home, label: 'Home' },
    { view: ViewState.CALENDAR, icon: Calendar, label: 'Calendar' },
    { view: ViewState.MEETINGS, icon: Briefcase, label: 'Meetings' },
    { view: ViewState.JOURNAL, icon: Book, label: 'Journal' },
    { view: ViewState.SETTINGS, icon: Settings, label: 'Settings' },
  ];

  return (
    <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40 safe-area-pb">
      <div className="flex items-center justify-around py-2">
        {navItems.map(item => (
          <button
            key={item.view}
            onClick={() => onNavigate(item.view)}
            className={`flex flex-col items-center py-2 px-3 rounded-lg transition-colors ${
              currentView === item.view 
                ? 'text-indigo-600' 
                : 'text-gray-400 hover:text-gray-600'
            }`}
          >
            <item.icon className="w-5 h-5" />
            <span className="text-[10px] mt-1 font-medium">{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// --- MOBILE DRAWER ---
const MobileDrawer = ({ 
  isOpen, 
  onClose, 
  currentView, 
  onNavigate, 
  userSettings, 
  stats,
  bookData,
  customStyling,
  user,
  isOwner,
  logout,
  navigate,
  profileMenuOpen,
  setProfileMenuOpen,
  setShowAuthModal,
  setShowProfileModal,
  setShowShareModal
}) => {
  if (!isOpen) return null;

  const navItems = [
    { view: ViewState.DASHBOARD, icon: LayoutDashboard, label: 'Overview' },
    { view: ViewState.JOURNAL, icon: Book, label: 'Journaling' },
    { view: ViewState.GOALS, icon: Target, label: 'Goal Setting', badge: stats?.activeGoals },
    { view: ViewState.CONTACTS, icon: Users, label: 'Contacts', badge: stats?.contacts },
    { divider: true, label: 'Work Space' },
    { view: ViewState.RECORDS, label: 'Records', icon: FileCheck, badge: stats?.records },
    //{ view: ViewState.MEETINGS, icon: Briefcase, label: 'Meetings', badge: stats?.meetings },
    { view: ViewState.CALENDAR, icon: Calendar, label: 'Calendar' },
    { view: ViewState.PLANNER, icon: ClipboardList, label: 'Planner', badge: stats?.plannerItems },
    // { view: ViewState.NOTES, icon: StickyNote, label: 'Quick Notes' },
    { view: ViewState.RESOURCES, icon: FolderOpen, label: 'Resources', badge: stats?.resources },
    { view: ViewState.LEARNING, icon: GraduationCap, label: 'Learning', badge: stats?.courses },
    { view: ViewState.TOOLS, icon: Wrench, label: 'Tools', badge: stats?.tools },
    { divider: true },
    { view: ViewState.SETTINGS, icon: Settings, label: 'Settings' },
  ];

  return (
    <>
    <div className="fixed inset-0 bg-black/50 z-40 md:hidden" onClick={onClose} /> 
     <div className="fixed sidebar-watermark inset-y-0 left-0 w-full z-50 md:hidden transform transition-transform" 
      style={{ backgroundColor: customStyling.primaryColor,
      "--favicon-url": `url('${bookData.favicon || "/favicon.png"}')`
       }} 
    > 
     
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="p-3 flex items-center justify-between border-b border-gray-800">
              <div className="flex items-center space-x-3">
                <HeaderLogoTitle
                  isOwner={isOwner}
                  bookData={bookData}
                  customStyling={customStyling}
                  navigate={navigate}
                />

                {/* User Profile */}
            <div className="p-4 border-t border-gray-800 space-y-3">
              
              {/* Fallback avatar always shows (same as header mobile icon) */}
              <div 
                className="flex items-center space-x-3 p-3 rounded-xl border border-gray-700/50 cursor-pointer"
                onClick={() => setProfileMenuOpen(!profileMenuOpen)}
              >
                {/* Avatar */}
                {user?.photoURL ? (
                  <img 
                    src={user.photoURL}
                    alt="Profile"
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold"
                    style={{ backgroundColor: customStyling.primaryColor }}
                  >
                    {(user?.displayName?.charAt(0) || userSettings?.userName?.charAt(0) || 'U').toUpperCase()}
                  </div>
                )}

                {setShowShareModal && (
                  <button
                    onClick={() => { setShowShareModal(true); onClose(); }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 hover:bg-indigo-700 text-gray-400 rounded-lg transition-colors text-sm font-medium"
                  >
                    <Share2 className="w-6 h-6" />
                  </button>
                )}
              </div>

              {/* UserProfile dropdown – matches header behaviour */}
              {profileMenuOpen && (
                <UserProfile
                  user={user}
                  styling={customStyling}
                  bookData={bookData}
                  isOwner={isOwner}
                  isDarkMode={true}
                  logout={logout}
                  setShowAuthModal={setShowAuthModal}
                  setShowProfileModal={setShowProfileModal}
                  setMobileMenuOpen={setProfileMenuOpen}
                  shouldShowUserProfile={() => true}
                />
              )}

            </div>
              </div>
              <button 
                onClick={onClose}
                className="absolute top-1/2 -right-1 z-50 w-10 h-10 bg-gray-100 border border-gray-200 rounded-full flex items-center justify-center shadow-lg text-gray-600 hover:text-indigo-600 hover:border-indigo-300 hover:scale-110 transition-all duration-200"
                style={{ color: customStyling.accentColor }}
              >
                <ChevronLeft size={16} /> 
              </button>
            </div>

            {/* Nav */}
            <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
              {navItems.map((item, i) => {
                if (item.divider) {
                  return (
                    <div key={i} className="pt-4 mt-4 border-t border-gray-800">
                      {item.label && (
                        <p className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                          {item.label}
                        </p>
                      )}
                    </div>
                  );
                }
                return (
                  <NavItem
                    key={item.view}
                    icon={item.icon}
                    label={item.label}
                    badge={item.badge}
                    customStyling={customStyling}
                    isActive={currentView === item.view}
                    onClick={() => { onNavigate(item.view); onClose(); }}
                  />
                );
              })}
            </nav>
          </div>
        </div>
    </>
  );
};

// --- MAIN LAYOUT ---
export const DiaryLayout = ({ 
  children, 
  currentView, 
  onNavigate, 
  userSettings = {},
  stats = {},
  // Auth & User props
  bookData = {},
  customStyling = {},
  user,
  isOwner,
  logout,
  navigate,
  // Modal setters
  setShowAuthModal,
  setShowProfileModal,
  setShowShareModal
}) => {
  const [isSidebarOpen, setSidebarOpen] = useState(true);
  const [isMobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const navItems = [
    { view: ViewState.DASHBOARD, icon: LayoutDashboard, label: 'Overview' },
    { view: ViewState.JOURNAL, icon: Book, label: 'Journaling' },
    { view: ViewState.GOALS, icon: Target, label: 'Goal Setting', badge: stats?.activeGoals },
    { view: ViewState.CONTACTS, icon: Users, label: 'Contacts', badge: stats?.contacts },
    { divider: true, label: 'Work Space' },
    { view: ViewState.RECORDS, label: 'Records', icon: FileCheck, badge: stats?.records },
    //{ view: ViewState.MEETINGS, icon: Briefcase, label: 'Meetings', badge: stats?.plannedMeetings },
    { view: ViewState.CALENDAR, icon: Calendar, label: 'Calendar' },
    { view: ViewState.PLANNER, icon: ClipboardList, label: 'Planner', badge: stats?.plannerItems },
    //{ view: ViewState.NOTES, icon: StickyNote, label: 'Quick Notes', badge: stats?.notes },
    { view: ViewState.RESOURCES, icon: FolderOpen, label: 'Resources', badge: stats?.resources },
    { view: ViewState.LEARNING, icon: GraduationCap, label: 'Learning', badge: stats?.courses },
    { view: ViewState.TOOLS, icon: Wrench, label: 'Tools', badge: stats?.tools },
  ];

  const faviconUrl = bookData?.favicon;

  return (
    <div className="flex h-screen bg-gray-100 overflow-hidden font-sans diary-sidebar-watermark">
      
      {/* Desktop Sidebar */}
      <aside 
        className={`hidden md:flex flex-row shrink-0 relative z-20 my-4 ml-6 h-[calc(100vh-2rem)] rounded-r-[2rem] shadow-2xl transition-all duration-500 ease-in-out ${
          isSidebarOpen ? 'w-80' : 'w-9'
        }`}
      >
        {/* Toggle Button */}
        <button 
          onClick={() => setSidebarOpen(!isSidebarOpen)}
          className="absolute top-6 -right-3 z-50 w-8 h-8 bg-white border border-gray-200 rounded-full flex items-center justify-center shadow-lg text-gray-600 hover:text-indigo-600 hover:border-indigo-300 hover:scale-110 transition-all duration-200"
          title={isSidebarOpen ? "Close Sidebar" : "Open Sidebar"}
        >
          {isSidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
        </button>
        
        {/* Spiral Binding */}
        <SpiralBinding ringCount={34} />

        {/* Sidebar Content */}
        <div
            className={`sidebar-watermark flex-1 flex flex-col h-full rounded-r-[1rem] overflow-hidden border-t border-b border-r border-gray-800/50 relative transition-all duration-500 origin-left ${
              isSidebarOpen ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0 w-0'
            }`}
            style={{
              backgroundColor: customStyling.primaryColor,
              "--favicon-url": `url('${bookData.favicon || "/favicon.png"}')`
            }}
          >
          {/* Gradient overlay */}
          <div className="absolute inset-y-0 left-0 w-4 bg-gradient-to-r from-black/60 to-transparent pointer-events-none z-10" />

          {/* Header with Logo/Title */}
          <div className="p-6 pb-4 relative z-10 whitespace-nowrap border-b border-gray-800/50">
              <HeaderLogoTitle
                isOwner={isOwner}
                bookData={bookData}
                customStyling={{ ...customStyling, primaryColor: '#ffffff' }}
                navigate={navigate}
              />
          </div>
          
          {/* Nav */}
          <nav className="flex-1 px-4 space-y-2 overflow-y-auto py-4 custom-scrollbar relative z-10">
            {navItems.map((item, i) => {
              if (item.divider) {
                return (
                  <div key={i} className="pt-4 mt-4 border-t border-gray-800">
                    {item.label && (
                      <p className="px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{item.label}</p>
                    )}
                  </div>
                );
              }
              return (
                <NavItem
                  key={item.view}
                  icon={item.icon}
                  label={item.label}
                  badge={item.badge}
                  customStyling={customStyling}
                  isActive={currentView === item.view}
                  onClick={() => onNavigate(item.view)}
                />
              );
            })}

            {/* Settings at bottom */}
            <div className="pt-4 mt-auto">
              <NavItem
                icon={Settings}
                label="Settings"
                customStyling={customStyling}
                isActive={currentView === ViewState.SETTINGS}
                onClick={() => onNavigate(ViewState.SETTINGS)}
              />
            </div>
          </nav>

          {/* User Profile */}
          <div className=" flex p-4 mt-auto bg-gradient-to-t from-black/40 to-transparent items-center relative z-10 space-y-3">   
            {/* User Profile Component */}
            {setShowAuthModal && setShowProfileModal ? (
              <UserProfile
                user={user}
                styling={customStyling}
                bookData={{ ...bookData, headerSettings: { showProfile: true } }}
                isOwner={isOwner}
                navigate={navigate}
                logout={logout}
                isDarkMode={true}
                setShowAuthModal={setShowAuthModal}
                setShowProfileModal={setShowProfileModal}
                mobileMenuOpen={false}
                setMobileMenuOpen={() => {}}
              />
            ) : (
              <div 
                className="flex items-center space-x-3 p-3 rounded-2xl bg-gray-800/50 border border-gray-700/50 backdrop-blur-sm hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap overflow-hidden" 
                onClick={() => onNavigate(ViewState.SETTINGS)}
              >
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shadow-inner ring-2 ring-gray-800 shrink-0 flex items-center justify-center text-white font-bold">
                  {(userSettings?.userName || user?.displayName || 'U').charAt(0).toUpperCase()}
                </div>
                <div className="overflow-hidden">
                  <p className="text-sm font-semibold text-white truncate">
                    {userSettings?.userName || user?.displayName || 'User'}
                  </p>
                  <p className="text-xs text-indigo-300 truncate">
                    {userSettings?.userRole || 'Member'}
                  </p>
                </div>
              </div>
            )}
            
              <button
                onClick={() => setShowShareModal(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium"
              >
                <Share2 className="w-4 h-4" />
                Share Diary
              </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="pt-0 lg:pt-6 flex-1 flex flex-col h-screen overflow-hidden relative transition-all duration-300">
        {/* Mobile Header */}
        <div className="md:hidden flex items-center p-4 text-white shrink-0 justify-between shadow-md z-30"
         style={{ backgroundColor: customStyling.primaryColor }}
        >
          <div className="flex items-center">
            <button onClick={() => setMobileDrawerOpen(true)} className="p-2 -ml-2 mr-2 hover:bg-gray-800 rounded-lg"
              style={{ color: customStyling.accentColor }}
              >
              <Menu className="w-6 h-6" />
            </button>
            {bookData?.logoUrl ? (
              <img 
                src={bookData.logoUrl} 
                alt={bookData?.title || 'Logo'} 
                className="h-8 w-auto object-contain"
                style={{ filter: `invert(${bookData?.styling?.logoInvert || 100}%)` }}
              />
            ) : (
              <span className="font-bold text-lg">{bookData?.title || bookData?.botName || 'LifeLog'}</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button className="p-2 text-gray-400 hover:bg-gray-800 rounded-lg">
              <Search className="w-5 h-5" />
            </button>
            <button className="p-2 text-gray-400 hover:bg-gray-800 rounded-lg relative">
              <Bell className="w-5 h-5" />
              {stats?.notifications > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
              )}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="pl-2 flex-1 overflow-hidden pb-16 md:pb-0">
          {children}
        </div>
      </main>

      {/* Mobile Drawer */}
      <MobileDrawer
        isOpen={isMobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        currentView={currentView}
        onNavigate={onNavigate}
        userSettings={userSettings}
        stats={stats}
        bookData={bookData}
        customStyling={customStyling}
        user={user}
        isOwner={isOwner}
        logout={logout}
        navigate={navigate}
        profileMenuOpen={profileMenuOpen}
        setProfileMenuOpen={setProfileMenuOpen} 
        setShowAuthModal={setShowAuthModal}
        setShowProfileModal={setShowProfileModal}
        setShowShareModal={setShowShareModal}
      />

      {/* Mobile Bottom Nav */}
      <MobileBottomNav currentView={currentView} onNavigate={onNavigate} />
    </div>
  );
};

export default DiaryLayout;